import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { Badge } from "@shared/ui/badge";
import { Eye, Mail, Phone, MessageSquare } from "lucide-react";
import { useGetAllWorkflowsExecutionDetails } from "@features/ux/components/workflow/hooks";

const ExecutionHistoryList = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read search query and page from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Replace this with your actual API hook
  const { data: executionsData } = useGetAllWorkflowsExecutionDetails(
    id,
    currentPage,
    MODAL_PAGE_SIZE,
    searchQuery
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: MODAL_PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = executionsData?.count || 0;
    const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE);
    const startItem = (currentPage - 1) * MODAL_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * MODAL_PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [executionsData?.count, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const handleViewDetails = (executionId: string) => {
    navigate(
      `/communication-hub/workflows/view/${executionId}/execution-details`
    );
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case "email":
        return <Mail className="w-4 h-4" />;
      case "phone":
        return <Phone className="w-4 h-4" />;
      case "sms":
        return <MessageSquare className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Define table columns
  const columns = useMemo<TableColumn<any>[]>(
    () => [
      {
        key: "executionDate",
        header: "Execution Date",
        sortable: true,
      },
      {
        key: "executionTime",
        header: "Execution Time",
        sortable: true,
      },
      {
        key: "recipientCount",
        header: "Recipient Count",
        sortable: true,
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "successCount",
        header: "Performance",
        sortable: false,
        render: (value: any, row: any) => (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Success:</span>
              <span className="text-sm font-semibold text-green-600">
                {value}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Failed:</span>
              <span className="text-sm font-semibold text-red-600">
                {row.failedCount}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "channels",
        header: "Channels",
        sortable: false,
        render: (value: string[]) => (
          <div className="flex gap-2">
            {value?.map((channel, index) => (
              <div
                key={index}
                className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                title={channel}
              >
                {getChannelIcon(channel)}
              </div>
            ))}
          </div>
        ),
      },
      {
        key: "actions",
        header: "Action",
        sortable: false,
        render: (_, row) => (
          <button
            onClick={() => handleViewDetails(row.id)}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5 text-gray-600" />
          </button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-6">
      <GenericTable
        data={executionsData?.results || []}
        columns={columns}
        rowKey="id"
        emptyMessage="No execution history found"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search executions..."
      />
    </div>
  );
};

export default ExecutionHistoryList;
